import { FC, useState } from 'react';
import { Box, Stack } from '@mui/material';
import Button from '@/components/button';
import FormSection from '@/components/form/form-section';
import { downloadFile, UiOption } from '@/lib/helpers';
import TextInput from '@/components/form/text-input';
import Text from '@/components/text';
import Select from '@/components/form/select';
import SurepathRootCert from '@/assets/surepath-ai-root-ca-09-2024.cer?raw';
import PageLink from '@/components/page-link';
import InlineToast from '@/components/toasts/inline';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const integrationConfig = {
  cert: {
    label: 'Root CA Certificate',
    filename: 'surepath-ai-root-ca-09-2024',
    text: 'Root CA Certificate',
  },
  edge: {
    label: 'Proxy URL',
    url: 'http://edge.surepath.ai:8080/',
  },
  proxyPAC: {
    label: 'Proxy PAC URL',
    url: 'https://pacfile.surepath.ai/default/current.pac',
  },
  doh: {
    label: 'DNS-over-HTTPS (DOH) Provider',
    url: 'https://doh.surepath.ai/dns-query',
  },
  portal: {
    label: 'App Portal',
    url: 'https://portal.surepath.ai/',
  },
  ready: {
    label: 'Readiness Check',
    url: 'https://ready.surepath.ai/',
  },
};

type CertificateFormat = 'cer' | 'pem' | 'crt';

const IntForm: FC = () => {
  const { cert, edge, proxyPAC, doh, portal, ready } = integrationConfig;

  const CertificateExtensions: UiOption[] = [
    { label: 'CER', value: 'cer' },
    { label: 'PEM', value: 'pem' },
    { label: 'CRT', value: 'crt' },
  ];

  const [downloadExtension, setDownloadExtension] = useState<CertificateFormat>('cer');

  const handleChange = (val: CertificateFormat) => {
    setDownloadExtension(val);
  };

  const handleDownloadCert = () => {
    if (!downloadExtension) {
      return;
    }

    const blob = new Blob([SurepathRootCert], { type: 'text/plain' });
    return downloadFile(blob, cert.filename + '.' + downloadExtension);
  };

  const canDownload = !!downloadExtension;

  return (
    <Box>
      <FormSection title="File Downloads" scrollId="file-downloads">
        <FormSectionSubtitle>
          Links to downloads for files required to integrate with F5 Workforce AI Security.
        </FormSectionSubtitle>
        <Box maxWidth="900px">
          <Text bold size="large">
            {cert.label}
          </Text>
          <Box my={2}>
            This certificate is needed to integrate with the platform. Depending on the integration
            type, it may need to be installed on endpoints, proxy servers, or SASE platforms.
          </Box>

          <Stack gap={3} direction="row" alignItems="center" mt={3}>
            <Box width="200px">
              <Select
                label="Certificate format"
                value={downloadExtension}
                name="download-format"
                options={CertificateExtensions}
                onChange={handleChange}
                size="small"
              />
            </Box>
            <Box>
              <Button
                label="Download Certificate"
                disabled={!canDownload}
                icon="download"
                onClick={handleDownloadCert}
              />
            </Box>
          </Stack>
        </Box>
      </FormSection>

      <FormSection title="Proxy and Provider URLs" scrollId="integration-urls">
        <FormSectionSubtitle>
          All the URLs necessary to implement the integration between a network or endpoints and the
          F5 Workforce AI Security platform.
        </FormSectionSubtitle>
        <Box maxWidth="900px">
          <Text bold size="large">
            {edge.label}
          </Text>
          <Box my={2}>
            This URL address is used for a proxy-based integration with Workforce AI Security using
            a SASE platform such as ZScaler, NetSkope, or Palo Alto Prisma Access.
          </Box>
          <Box mb={2}>
            <TextInput name="edgeURL" label={edge.label} readonly value={edge.url} copyClip />
          </Box>

          <Text bold size="large">
            {proxyPAC.label}
          </Text>
          <Box my={2}>
            Workforce AI Security provides a direct endpoint integration via proxy PAC URL. It can
            be manually installed on devices for testing purposes or distributed by administrators
            using management tools such as Microsoft Intune, JAMF, or Active Directory Group Policy.
            An admin can generate unique proxy PAC URLs for the organization by adding Proxy
            connectors on the <PageLink routeKey="connectors" /> page.
          </Box>

          <Text bold size="large">
            {doh.label}
          </Text>
          <Box my={2}>
            This URL address is used for a DNS-based integration with Workforce AI Security. This
            can be used at the operating system level or within a browser such as Google Chrome.
            This DOH provider will steer all generative AI requests to the platform.
            <Box mt={2}>
              <InlineToast
                variant="outlined"
                message="The DOH integration is intended for small-scale testing/demo purposes. It should not be
              used for a production integration."
              />
            </Box>
          </Box>
          <Box mb={2}>
            <TextInput name="dohURL" label={doh.label} readonly value={doh.url} copyClip />
          </Box>
        </Box>
      </FormSection>

      <FormSection title="Website URLs" scrollId="website-urls">
        <FormSectionSubtitle>
          Links to important websites that are part of the F5 Workforce AI Security platform.
        </FormSectionSubtitle>
        <Box maxWidth="900px">
          <Text bold size="large">
            {portal.label}
          </Text>
          <Box my={2}>This is the URL for the Workforce AI Security App Portal.</Box>
          <Box mb={2}>
            <TextInput
              name="portalURL"
              label={portal.label}
              readonly
              value={portal.url}
              copyClip
              openUrl
            />
          </Box>

          <Text bold size="large">
            {ready.label}
          </Text>
          <Box my={2}>
            The Workforce AI Security Readiness Check, or Ready for short, allows administrators to
            verify that the integration is working as expected and that generative AI traffic is
            being routed through the platform.
          </Box>
          <Box mb={2}>
            <TextInput
              name="readyURL"
              label={ready.label}
              readonly
              value={ready.url}
              copyClip
              openUrl
            />
          </Box>
        </Box>
      </FormSection>
    </Box>
  );
};

export default IntForm;
